import {
  ICreateNote,
  IRemoveNote,
  IUpdateNote,
  IUpdateActivityTrack,
  IUpdateSectionTrack
} from '@sdk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

import { sdk } from '@/services';

const ActivityTrackingService = sdk.getService('ActivityTrackingService');
const SectionTrackingService = sdk.getService('SectionTrackingService');
const ActivityNoteService = sdk.getService('ActivityNoteService');
const SubscriptionService = sdk.getService('SubscriptionService');

export const useMutateMySubscriptions = () => {
  const { t } = useTranslation('StudentCourseTranslations');
  const queryClient = useQueryClient();

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    queryClient.invalidateQueries({ queryKey: ['planification'] });
  };

  const { mutate: updateActivityTrack, isPending: isUpdatingActivityTrack } = useMutation({
    mutationFn: (update: IUpdateActivityTrack) =>
      ActivityTrackingService.updateActivityTrack(update),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['subscriptions', variables.subscriptionId],
        exact: true
      });

      queryClient.invalidateQueries({
        queryKey: ['subscriptions'],
        exact: true
      });

      if (variables.activityTrack.isCompleted) {
        queryClient.invalidateQueries({ queryKey: ['planification'] });
      }
    }
  });

  const { mutateAsync: toggleSubscriptionVisibility, isPending: isToggling } = useMutation({
    mutationFn: (subscriptionId: string) =>
      SubscriptionService.toggleSubscriptionVisibility(subscriptionId),
    onSuccess
  });

  const { mutate: createNote, isPending: isCreatingNote } = useMutation({
    mutationFn: (note: ICreateNote) => ActivityNoteService.create(note),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['subscriptions', variables.subscriptionId],
        exact: true
      });
      message.success(t('NOTE_CREATED'));
    },
    onError: () => message.error(t('NOTE_ERROR'))
  });

  const { mutate: updateNote, isPending: isUpdatingNote } = useMutation({
    mutationFn: (note: IUpdateNote) => ActivityNoteService.update(note),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['subscriptions', variables.subscriptionId],
        exact: true
      });
      message.success(t('NOTE_UPDATED'));
    },
    onError: () => message.error(t('NOTE_ERROR'))
  });

  const { mutate: removeNote, isPending: isRemovingNote } = useMutation({
    mutationFn: (note: IRemoveNote) => ActivityNoteService.remove(note),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['subscriptions', variables.subscriptionId],
        exact: true
      });
      message.warning(t('NOTE_REMOVED'));
    },
    onError: () => message.error(t('NOTE_ERROR'))
  });

  const { mutate: updateSectionTrack, isPending: isUpdatingSectionTrack } = useMutation({
    mutationFn: (update: IUpdateSectionTrack) => SectionTrackingService.updateSectionTrack(update),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['subscriptions', variables.subscriptionId],
        exact: true
      });
    }
  });

  const { mutateAsync: setMyVisibleSubscriptions, isPending: isSettingMyVisibleSubscriptions } =
    useMutation({
      mutationFn: (subscriptionIds: string[]) =>
        SubscriptionService.setMyVisibleSubscriptions(subscriptionIds),
      onSuccess
    });

  return {
    updateActivityTrack,
    toggleSubscriptionVisibility,
    createNote,
    updateNote,
    removeNote,
    updateSectionTrack,
    setMyVisibleSubscriptions,
    isWorking:
      isUpdatingActivityTrack ||
      isToggling ||
      isCreatingNote ||
      isUpdatingNote ||
      isRemovingNote ||
      isUpdatingSectionTrack ||
      isSettingMyVisibleSubscriptions
  };
};
